import { NavLink, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { bottomTabs } from './nav-config';

interface BottomTabBarProps {
  onMoreClick: () => void;
  badges?: Record<string, number>;
  /** FM3 fix — aggregate of the ambient (BR-14: no bell entry) unseen-change counts that
   *  live behind the "More" menu (leasing/agent-review/escalation/expense). Since ambient
   *  classes have no other nav-level surfacing, this is what tells a mobile landlord
   *  there's unseen activity worth opening "More" for. Detail per class is in MoreMenu. */
  moreUnseenCount?: number;
}

export function BottomTabBar({ onMoreClick, badges = {}, moreUnseenCount = 0 }: BottomTabBarProps) {
  const location = useLocation();

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-background border-t border-border"
      aria-label="Main navigation"
    >
      <div
        className="flex items-stretch"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        {bottomTabs.map((tab) => {
          const isMore = tab.path === null;
          const isActive = !isMore && location.pathname.startsWith(tab.path!);
          const badge = badges[tab.key];
          const Icon = tab.icon;

          if (isMore) {
            return (
              <button
                key={tab.key}
                type="button"
                onClick={onMoreClick}
                className={cn(
                  'flex-1 flex flex-col items-center justify-center gap-0.5 py-2 min-h-[var(--bottom-tab-height)]',
                  'text-muted-foreground transition-colors',
                )}
                aria-label={
                  moreUnseenCount > 0
                    ? `More navigation options — ${moreUnseenCount} unseen update${moreUnseenCount === 1 ? '' : 's'}`
                    : 'More navigation options'
                }
              >
                <div className="relative">
                  <Icon className="h-5 w-5" />
                  {moreUnseenCount > 0 && (
                    <span
                      className="absolute -top-1 -right-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[9px] font-bold text-destructive-foreground"
                      aria-hidden="true"
                    >
                      {moreUnseenCount > 99 ? '99+' : moreUnseenCount}
                    </span>
                  )}
                </div>
                <span className="text-[10px] font-medium">{tab.label}</span>
              </button>
            );
          }

          return (
            <NavLink
              key={tab.key}
              to={tab.path!}
              className={cn(
                'flex-1 flex flex-col items-center justify-center gap-0.5 py-2 min-h-[var(--bottom-tab-height)]',
                'transition-colors relative',
                isActive ? 'text-primary' : 'text-muted-foreground',
              )}
              aria-current={isActive ? 'page' : undefined}
              aria-label={
                badge != null && badge > 0
                  ? `${tab.label}, ${badge > 99 ? '99+' : badge} unread`
                  : undefined
              }
            >
              <div className="relative">
                <Icon className="h-5 w-5" />
                {badge != null && badge > 0 && (
                  <span
                    className="absolute -top-1 -right-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[9px] font-bold text-destructive-foreground"
                    aria-hidden="true"
                  >
                    {badge > 99 ? '99+' : badge}
                  </span>
                )}
              </div>
              <span className="text-[10px] font-medium">{tab.label}</span>
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
}
